import { useCallback, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link2, Image as ImageIcon,
  Undo2, Redo2, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { uploadMedia } from "@/lib/api";

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB per image (server caps same)

function ToolbarBtn({ onClick, active, title, disabled, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      data-testid={testid}
      className={[
        "h-8 w-8 inline-grid place-items-center rounded text-zinc-600",
        "hover:bg-zinc-100 hover:text-zinc-950",
        "disabled:opacity-40 disabled:pointer-events-none",
        active ? "bg-zinc-900 text-white hover:bg-zinc-900 hover:text-white" : "",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Separator() {
  return <span className="h-5 w-px bg-zinc-200 mx-1" />;
}

export default function RichTextEditor({ value, onChange }) {
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: "rt-img" },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "prose-rt min-h-[280px] max-h-[70vh] overflow-y-auto focus:outline-none " +
          "px-4 py-4 text-[15px] leading-relaxed text-zinc-900",
        "data-testid": "richtext-editor-surface",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Keep editor in sync if parent replaces value externally (e.g. load existing)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (typeof value === "string" && value !== current) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  const addImage = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow picking the same file again
    if (!file || !editor) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image must be under ${(MAX_IMAGE_BYTES / 1024 / 1024).toFixed(0)} MB`);
      return;
    }
    const toastId = toast.loading("Uploading image…");
    try {
      const up = await uploadMedia(file);
      editor.chain().focus().setImage({ src: up.url, alt: file.name }).run();
      toast.success("Image added", { id: toastId });
    } catch {
      toast.error("Upload failed", { id: toastId });
    }
  };

  const setLink = () => {
    if (!editor) return;
    const prev = editor.getAttributes("link")?.href || "";
    const url = window.prompt("URL (leave empty to remove)", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    let href = url.trim();
    if (!/^(https?:|mailto:|tel:)/i.test(href)) href = `https://${href}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  if (!editor) {
    return <div className="h-[320px] grid place-items-center text-zinc-400">Loading editor…</div>;
  }

  return (
    <div className="border border-zinc-200 rounded-md bg-white overflow-hidden" data-testid="richtext-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-zinc-200 bg-zinc-50 px-2 py-1.5">
        <ToolbarBtn
          title="Bold" testid="rt-bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Italic" testid="rt-italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Underline" testid="rt-underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Strikethrough" testid="rt-strike"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough className="h-4 w-4" />
        </ToolbarBtn>

        <Separator />

        <ToolbarBtn
          title="Heading 1" testid="rt-h1"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Heading 2" testid="rt-h2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Heading 3" testid="rt-h3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" />
        </ToolbarBtn>

        <Separator />

        <ToolbarBtn
          title="Bullet list" testid="rt-ul"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Numbered list" testid="rt-ol"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Quote" testid="rt-quote"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Code block" testid="rt-code"
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          <Code className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Horizontal rule" testid="rt-hr"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-4 w-4" />
        </ToolbarBtn>

        <Separator />

        <ToolbarBtn
          title="Link" testid="rt-link"
          active={editor.isActive("link")}
          onClick={setLink}>
          <Link2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn
          title="Insert image" testid="rt-image"
          onClick={addImage}>
          <ImageIcon className="h-4 w-4" />
        </ToolbarBtn>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickFile}
          data-testid="rt-image-file-input"
        />

        <div className="ml-auto flex items-center">
          <ToolbarBtn
            title="Undo" testid="rt-undo"
            disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}>
            <Undo2 className="h-4 w-4" />
          </ToolbarBtn>
          <ToolbarBtn
            title="Redo" testid="rt-redo"
            disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}>
            <Redo2 className="h-4 w-4" />
          </ToolbarBtn>
        </div>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
